<template>
  <div class="tally-export-page py-3">
    <header class="tx-toolbar">
      <div class="tx-toolbar__title">
        <span class="tx-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Tally Export</h1>
          <p>Download Tally-ready sheets for a date range</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="tx-alert">{{ errorMessage }}</CAlert>

    <section class="tx-section">
      <header class="tx-section__head">
        <h2>Date Range</h2>
        <small class="tx-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="tx-section__body">
        <div class="tx-filters">
          <div class="tx-field tx-field--range">
            <label class="tx-label">Period</label>
            <DateRangePicker v-model="dateRange" variant="form" :presets="false" />
          </div>
          <div class="tx-field tx-field--presets">
            <label class="tx-label" aria-hidden="true">&nbsp;</label>
            <div class="tx-presets" role="group" aria-label="Quick date ranges">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="tx-preset"
                :class="{ 'is-active': activePreset === preset.key }"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="tx-section">
      <header class="tx-section__head">
        <h2>Exports</h2>
        <small class="tx-section__hint">Tap a tile to download the .xlsx sheet</small>
      </header>
      <div class="tx-section__body">
        <div class="tx-grid">
          <div v-for="item in items" :key="item.api" class="tx-tile">
            <div class="tx-tile__icon" :class="`tx-tile__icon--${item.tone}`" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path :d="item.iconPath" />
                <path v-if="item.iconPath2" :d="item.iconPath2" />
              </svg>
            </div>
            <div class="tx-tile__body">
              <div class="tx-tile__title">{{ item.title }}</div>
              <div class="tx-tile__hint">{{ item.hint }}</div>
            </div>
            <button
              type="button"
              class="tx-download-btn"
              :class="{ 'is-loading': loadingMap[item.api] }"
              :disabled="loadingMap[item.api]"
              @click="download(item)"
            >
              <svg v-if="!loadingMap[item.api]" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{{ loadingMap[item.api] ? 'Downloading…' : 'Download' }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import DateRangePicker from '@/components/DateRangePicker.vue'

export default {
  name: 'TallyExport',
  components: { DateRangePicker },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    return {
      fromDate: today,
      toDate: today,
      errorMessage: '',
      loadingMap: {},
      presets: [
        { key: 'today', label: 'Today' },
        { key: '7d', label: 'Last 7 Days' },
        { key: '30d', label: 'Last 30 Days' },
        { key: '6m', label: 'Last 6 Months' },
        { key: 'last-fy', label: 'Last Financial Year' }
      ],
      items: [
        {
          title: 'Invoice',
          hint: 'Sales invoices for the period',
          api: '/api/tallyex-invoice',
          filename: 'tally_invoice_',
          tone: 'green',
          iconPath: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z',
          iconPath2: 'M14 2v6h6M8 13h8M8 17h5'
        },
        {
          title: 'Credit Notes',
          hint: 'Returns and credit adjustments',
          api: '/api/tallyex-credit-note',
          filename: 'tally_credit_note_',
          tone: 'amber',
          iconPath: 'M3 12a9 9 0 1 0 9-9',
          iconPath2: 'M3 4v5h5'
        },
        {
          title: 'Payment',
          hint: 'Payments received in the period',
          api: '/api/tallyex-payment',
          filename: 'tally_payment_',
          tone: 'blue',
          iconPath: 'M2 7h20v10H2z',
          iconPath2: 'M2 11h20M6 15h2'
        }
      ]
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || ''
        this.toDate = (value && value.toDate) || ''
      }
    },
    rangeLabel () {
      if (!this.fromDate || !this.toDate) return ''
      if (this.fromDate === this.toDate) return this.formatDisplay(this.fromDate)
      return `${this.formatDisplay(this.fromDate)} → ${this.formatDisplay(this.toDate)}`
    },
    activePreset () {
      if (!this.fromDate || !this.toDate) return ''
      for (const preset of this.presets) {
        const range = this.rangeForPreset(preset.key)
        if (range && range.fromDate === this.fromDate && range.toDate === this.toDate) {
          return preset.key
        }
      }
      return ''
    }
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    formatDisplay (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      if (!y || !m || !d) return iso
      return `${d}-${m}-${y}`
    },
    toIso (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    rangeForPreset (key) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      const end = new Date(today)
      switch (key) {
        case 'today':
          break
        case '7d':
          start.setDate(today.getDate() - 6)
          break
        case '30d':
          start.setDate(today.getDate() - 29)
          break
        case '6m':
          start.setMonth(today.getMonth() - 6)
          start.setDate(start.getDate() + 1)
          break
        case 'last-fy': {
          // Indian FY: April 1 → March 31. "Last" = most recently completed FY.
          const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() - 1 : today.getFullYear() - 2
          start.setFullYear(fyStartYear, 3, 1)
          end.setFullYear(fyStartYear + 1, 2, 31)
          break
        }
        default:
          return null
      }
      return { fromDate: this.toIso(start), toDate: this.toIso(end) }
    },
    applyPreset (key) {
      const range = this.rangeForPreset(key)
      if (!range) return
      this.fromDate = range.fromDate
      this.toDate = range.toDate
    },
    download (item) {
      this.errorMessage = ''
      if (!this.fromDate || !this.toDate) {
        this.errorMessage = 'From Date and To Date are required.'
        return
      }

      this.$set(this.loadingMap, item.api, true)
      axios({
        url: `${this.$apiAdress}${item.api}?token=${this.authToken()}`,
        method: 'POST',
        data: { fromDate: this.fromDate, toDate: this.toDate },
        responseType: 'blob'
      })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
          const anchor = document.createElement('a')
          anchor.href = blobUrl
          anchor.setAttribute('download', `${item.filename}${this.fromDate}-${this.toDate}.xlsx`)
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
        })
        .catch(async (error) => {
          console.log(error)
          const status = error && error.response && error.response.status
          let detail = ''
          const blob = error && error.response && error.response.data
          if (blob && typeof blob.text === 'function') {
            try {
              const text = await blob.text()
              try {
                const parsed = JSON.parse(text)
                detail = parsed.message || parsed.error || parsed.exception || text
              } catch (e) {
                detail = text
              }
            } catch (e) {
              detail = ''
            }
          } else if (error && error.message) {
            detail = error.message
          }
          if (detail && detail.length > 400) detail = detail.slice(0, 400) + '…'
          const prefix = status ? `Unable to download (HTTP ${status})` : 'Unable to download export file.'
          this.errorMessage = detail ? `${prefix}: ${detail}` : prefix
        })
        .finally(() => {
          this.$set(this.loadingMap, item.api, false)
        })
    }
  }
}
</script>

<style scoped>
.tally-export-page {
  padding: 0.25rem 0;
}

.tx-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.tx-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.tx-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tx-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.tx-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.tx-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.tx-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.tx-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.tx-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tx-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.tx-section__body {
  padding: 1rem;
}

.tx-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.tx-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  min-height: 36px;
  align-items: center;
}

.tx-preset {
  display: inline-flex;
  align-items: center;
  height: 30px;
  padding: 0 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.tx-preset:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.tx-preset:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.tx-preset.is-active {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border-color: transparent;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
}

.tx-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.tx-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 320px;
  min-width: 0;
}

.tx-field--range {
  flex: 0 1 460px;
  max-width: 480px;
}

.tx-field--presets {
  flex: 1 1 320px;
  min-width: 0;
}

.tx-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.85rem;
}

.tx-tile {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.tx-tile:hover {
  border-color: #c8e6d8;
  box-shadow: 0 6px 16px rgba(14, 91, 70, 0.08);
  transform: translateY(-1px);
}

.tx-tile__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
}

.tx-tile__icon--green {
  background: #dff5ea;
  color: #0e5b46;
}

.tx-tile__icon--amber {
  background: #fef3c7;
  color: #92400e;
}

.tx-tile__icon--blue {
  background: #e0edff;
  color: #1e40af;
}

.tx-tile__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.tx-tile__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tx-tile__hint {
  font-size: 0.78rem;
  color: #6b7280;
}

.tx-download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  cursor: pointer;
  align-self: flex-start;
}

.tx-download-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.tx-download-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.tx-download-btn:disabled,
.tx-download-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}
</style>
